// Copyright 2013-2020 The MathWorks, Inc.
/**
 * This class is responsible for keeping track of all the endPoints with which remote layer
 * communicates. It provides a uniform API to create new endpoint, associate messages with that
 * endpoint, enable or disable the endpoint, create iframeProxy, for the endPoint
 */
define([
    'dojo/_base/declare',
    'dojo/_base/array',
    'dojo/_base/Deferred',
    'dojo/_base/lang',
    './iframeproxy/IframeProxyClient'
], function (declare, arrayUtils, Deferred, lang, IframeProxyClient) {
    return declare([], {
        /**
         * Initializes the endPoints Collection.
         */
        constructor: function () {
            this.DirectXhrConstants = {
                ENABLED: 'ENABLED',
                DISABLED: 'DISABLED',
                UNRESOLVED: 'UNRESOLVED'
            };
            this._setDirectXhrCommunicationState(false);
            this._directXhrChecks = {};
            this._initializeEndPointsCollection();
        },

        /**
         * Sets up the endPoints
         * @private
         */
        _initializeEndPointsCollection: function () {
            this.endPointsCollection = {
                ROUTER_LEAF_INSECURE_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: true,
                    iframeProxyUrl: '',
                    pathSuffix: 'messageservice/json',
                    messages: ['RouterLogin', 'CreateAccount', 'CurrentUser', 'Login',
                        'UpdateAccount', 'ServiceRegistry', 'ClientType', 'AuthorizationDetails', 'AcquireMatlab'],
                    iframeProxyName: 'remoteProducer',
                    acceptsMessageContainerMessages: true,
                    directXhr: this.DirectXhrConstants.UNRESOLVED },
                ROUTER_LEAF_SECURE_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: true,
                    iframeProxyUrl: '',
                    pathSuffix: 'messageservice/json/secure',
                    messages: [],
                    iframeProxyName: 'remoteProducer',
                    acceptsMessageContainerMessages: true,
                    directXhr: this.DirectXhrConstants.UNRESOLVED },
                WORKER_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: false,
                    iframeProxyUrl: '',
                    pathSuffix: 'messageservice/json/secure',
                    messages: [],
                    iframeProxyName: 'remoteWorkerProducer',
                    acceptsMessageContainerMessages: true,
                    directXhr: this.DirectXhrConstants.UNRESOLVED },
                WORKER_ASYNC_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: true,
                    iframeProxyUrl: '',
                    pathSuffix: 'messageservice/async',
                    messages: [],
                    iframeProxyName: 'remoteWorkerAsyncProducer',
                    acceptsMessageContainerMessages: true,
                    directXhr: this.DirectXhrConstants.UNRESOLVED },
                USAGE_DATA_ROUTER_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: true,
                    iframeProxyUrl: '',
                    pathSuffix: 'udc/service/v1/events',
                    messages: ['UsageDataEventCollection'],
                    iframeProxyName: 'remoteUDCProducer',
                    acceptsMessageContainerMessages: false,
                    directXhr: this.DirectXhrConstants.UNRESOLVED },
                HELP_DATA_ROUTER_ENDPOINT: { routingHost: '',
                    routingKey: '',
                    iframeProxy: null,
                    enabled: true,
                    iframeProxyUrl: '',
                    pathSuffix: '',
                    messages: ['HelpDataEventCollection'],
                    iframeProxyName: 'doc_center',
                    acceptsMessageContainerMessages: false,
                    directXhr: this.DirectXhrConstants.UNRESOLVED }

            };
        },

        /**
         * Feature switch for checking if Direct XHR communication can be done
         * @param state: True will enable use of Direct XHR when possible, False, will force Iframe for communication
         */
        _setDirectXhrCommunicationState: function (state) {
            this._useDirectXhrForCommunication = state;
        },

        /**
         * This function is used to rest the EndPointsManager's endPointsCollection.
         */
        reset: function () {
            this._initializeEndPointsCollection();
            this._directXhrChecks = {};
        },

        /**
         * This can be used to set up an endPoint during runtime.
         * @param endPointName : Name of the endPoint
         * @param isEnabled : During the initialization, the endPoint can be enabled or disabled
         * using this param
         * @param pathSuffix : This is the suffix of the URL to which we post messages.
         * @param IframeProxyName: Name of the iframe created.
         * @param isMessageContainerBased: This endpoint can be used to determine if it is message container Based or not
         */
        setUpNewEndPoint: function (endPointName, isEnabled, pathSuffix, IframeProxyName,
            isMessageContainerBased) {
            this.endPointsCollection[endPointName] = { routingHost: '',
                routingKey: '',
                pathSuffix: pathSuffix,
                enabled: isEnabled,
                messages: [],
                iframeProxy: null,
                iframeProxyName: IframeProxyName,
                acceptsMessageContainerMessages: isMessageContainerBased,
                directXhr: this.DirectXhrConstants.UNRESOLVED };
        },

        /**
         * returns the suffix of endPoint or URL.
         * @param endPointName
         * @return {string}
         */
        getPathSuffixForEndPoint: function (endPointName) {
            return this.endPointsCollection[endPointName].pathSuffix;
        },

        /**
         * set the suffix for endPoint or URL.
         * @param endPointName
         * @param pathSuffix
         */
        setPathSuffixForEndPoint: function (endPointName, pathSuffix) {
            this.endPointsCollection[endPointName].pathSuffix = pathSuffix;
        },

        /**
         * This function returns array of endPoint names. This ensures that endPointsCollection is
         * not modified outside this manager.
         * @return {} - Object containing endPointNames
         */
        getEndPointNames: function () {
            var endPointsEnumeration = {}; var endPointName;
            for (endPointName in this.endPointsCollection) {
                if (this.endPointsCollection.hasOwnProperty(endPointName)) {
                    endPointsEnumeration[endPointName] = endPointName;
                }
            }
            return endPointsEnumeration;
        },

        /**
         * This function is used to set Routing Host and Routing Key for the endPoint. IframeProxy and directXhr property
         * is also set up as a part of it.
         * @param endPointName
         * @param routingHost
         * @param routingKey
         */
        setRoutingHostAndRoutingKey: function (endPointName, routingHost, routingKey) {
            if (routingHost && routingHost.lastIndexOf('/') !== routingHost.length - 1) {
                routingHost += '/';
            }
            this.endPointsCollection[endPointName].routingHost = routingHost;

            if (routingKey) {
                this.endPointsCollection[endPointName].routingKey = routingKey;
            }
            this._checkIfEndpointAllowsDirectXhr(endPointName, true).then(
                lang.hitch(this, this._processEndPointAllowsXhrResponse, endPointName, routingHost)
            );
        },

        /**
         * This function is used to process the response of  _checkIfEndpointAllowsDirectXhr function.
         * @param endPointName
         * @param routingHost
         * @param result the result of the _checkIfEndpointAllowsDirectXhr
         * @private
         */
        _processEndPointAllowsXhrResponse: function (endPointName, routingHost, result) {
            this._setEndpointAllowsDirectXhr(result);
            if (result.status !== this.DirectXhrConstants.ENABLED && routingHost) {
                this._setIframeRoutingHostAndRoutingkey(endPointName);
            }
        },

        /**
         * This function is used to set up the directXhr property for the endPoint.
         * @param result the result of the _checkIfEndpointAllowsDirectXhr
         * @private
         */
        _setEndpointAllowsDirectXhr: function (result) {
            if (this.endPointsCollection[result.endPointName] && result.status in this.DirectXhrConstants) {
                this.endPointsCollection[result.endPointName].directXhr = result.status;
            }
        },

        /**
         * This function is used to set up the iframe for the endPoint.
         * @param endPointName
         * @private
         */
        _setIframeRoutingHostAndRoutingkey: function (endPointName) {
            var iframeProxyClient = this.getIframeProxyClientForEndPoint(endPointName);
            if (iframeProxyClient) {
                iframeProxyClient.setRemotePageHost(
                    this.endPointsCollection[endPointName].routingHost,
                    this.endPointsCollection[endPointName].routingKey, null,
                    this.endPointsCollection[endPointName].iframeProxyUrl);
            }
        },

        /**
         * This function is used to get the directXhr for the end point
         * @param endPointName
         * @private
         */
        getEndPointAllowsDirectXhr: function (endpointName) {
            return this._checkIfEndpointAllowsDirectXhr(endpointName, false);
        },

        /**
         * This method is used to check whether Direct XHR is supported for an endpoint or not.
         * @param {object} endPointName - endPointName to be checked
         * @param {object} forceRenew - whether or not this is the first check for an endPoint
         * @return {*} a promise
         * @private
         */
        _checkIfEndpointAllowsDirectXhr: function (endPointName, forceRenew) {
            if (this._directXhrChecks[endPointName] && !forceRenew) {
                return this._directXhrChecks[endPointName];
            }

            var deferred = new Deferred();
            this._directXhrChecks[endPointName] = deferred;
            var directXhrCheck = this.getDirectXhrAllowed(endPointName);
            var DirectXhrConstants = this.DirectXhrConstants;
            var endPointResult = {
                endPointName: endPointName,
                status: DirectXhrConstants.UNRESOLVED
            };
            var url;
            if (typeof URL === 'function') {
                /**
                 * Wrap under try/catch, and disable direct XHR if exception occurs, rationale
                 * is that if endpoint is not known then Direct XHR is not allowed/possible
                 */
                try {
                    url = new URL(this.getRoutingHost(endPointName));
                } catch (exception) {
                    endPointResult.status = DirectXhrConstants.DISABLED;
                    deferred.resolve(endPointResult);
                    return deferred.promise;
                }
            } else {
                url = document.createElement('a');
                url.href = this.getRoutingHost(endPointName);
            }

            // If direct XHR feature is switched on, then resume regular workflow
            if (this._useDirectXhrForCommunication) {
                if (url.origin === window.location.origin) {
                    endPointResult.status = DirectXhrConstants.ENABLED;
                    deferred.resolve(endPointResult);
                } else {
                    if (directXhrCheck === DirectXhrConstants.UNRESOLVED) {
                        var directXhrCheckRequest = new XMLHttpRequest();
                        var directXhrCheckRequestUrl = this.getRemoteUrlForEndPoint(endPointName);
                        directXhrCheckRequest.open('OPTIONS', directXhrCheckRequestUrl);
                        directXhrCheckRequest.send();
                        directXhrCheckRequest.onload = function () {
                            if (directXhrCheckRequest.status === 200) {
                                endPointResult.status = DirectXhrConstants.ENABLED;
                                deferred.resolve(endPointResult);
                            } else {
                                endPointResult.status = DirectXhrConstants.DISABLED;
                                deferred.resolve(endPointResult);
                            }
                        };
                        directXhrCheckRequest.onerror = function () {
                            endPointResult.status = DirectXhrConstants.DISABLED;
                            deferred.resolve(endPointResult);
                        };
                    } else {
                        endPointResult.status = directXhrCheck;
                        deferred.resolve(endPointResult);
                    }
                }
            } else {
                // If direct XHR feature is switched off, don't do pre-flights
                endPointResult.status = DirectXhrConstants.DISABLED;
                deferred.resolve(endPointResult);
            }
            return deferred.promise;
        },

        /**
         * Returns routingHost for the endPoint.
         * @param endPointName
         * @return routingHost
         */
        getRoutingHost: function (endPointName) {
            return this.endPointsCollection[endPointName].routingHost;
        },

        /**
         * Returns Routing Key for the endPoint.
         * @param endPointName
         * @return {}
         */
        getRoutingKey: function (endPointName) {
            return this.endPointsCollection[endPointName].routingKey;
        },

        /**
         * Returns messageTypes that are mapped to endPoint
         * @param endPointName
         * @return {*}
         */
        getMessageTypesMappedToEndPoint: function (endPointName) {
            return this.endPointsCollection[endPointName].messages;
        },

        /**
         * Returns boolean if the endPoint is enabled or not.
         * @param endPointName
         * @return boolean : true | false
         */
        isEndPointEnabled: function (endPointName) {
            return this.endPointsCollection[endPointName].enabled;
        },

        /**
         * Maps message types to endPoints
         * @param endPointName
         * @param messages
         */
        setMessageTypesToEndPoint: function (endPointName, messages) {
            this.endPointsCollection[endPointName].messages = messages;
        },

        /**
         * Sets the endPoint's isEnabled status to true
         * @param endPointName
         */
        enableEndPoint: function (endPointName) {
            this.endPointsCollection[endPointName].enabled = true;
        },

        /**
         * Returns boolean if cors endPoint is enabled or not.
         * @param endPointName
         * @return constant : DISABLED | ENABLED | UNRESOLVED
         */
        getDirectXhrAllowed: function (endPointName) {
            return this.endPointsCollection[endPointName].directXhr;
        },

        /**
         * Disables endPoint
         * @param endPointName
         */
        disableEndPoint: function (endPointName) {
            this.endPointsCollection[endPointName].enabled = false;
        },

        /**
         * Gets appropriate endPoint based on the messageType passed. If an enabled endPoint is not
         * found, default endpoint ROUTER_LEAF_SECURE_ENDPOINT is returned.
         * @param messageType
         * @return String
         */
        getEndPointForMessageType: function (messageType) {
            var endPointType = null; var endPointName;
            for (endPointName in this.endPointsCollection) {
                if (this.endPointsCollection.hasOwnProperty(endPointName)) {
                    endPointType = arrayUtils.lastIndexOf(
                        this.endPointsCollection[endPointName].messages,
                        messageType) > -1 ? endPointName : endPointType;
                    if (endPointType) {
                        if (this.isEndPointEnabled(endPointType)) {
                            break;
                        } else {
                            // Set it back to null so that the default routing is applied.
                            endPointType = null;
                        }
                    }
                }
            }
            return (
                endPointType || this.getEndPointNames().ROUTER_LEAF_SECURE_ENDPOINT);
        },

        /**
         * Returns complete URL to which we must post messages.
         * @param endPointName
         * @return {*} String
         */
        getRemoteUrlForEndPoint: function (endPointName) {
            var url = this.endPointsCollection[endPointName].routingHost +
                this.endPointsCollection[endPointName].pathSuffix;
            if (this.endPointsCollection[endPointName].routingKey) {
                url = url + '?routingkey=' + this.endPointsCollection[endPointName].routingKey;
            }
            return url;
        },

        /**
         * Sets iframeProxyClient for the endPoint.
         * @param endPointName
         * @param iframeProxyClient
         */
        setIframeProxyClientForEndPoint: function (endPointName, iframeProxyClient) {
            this.endPointsCollection[endPointName].iframeProxy = iframeProxyClient;
        },

        /**
         * gets IframeProxyClient for the endpoint.
         * @param endPointName
         * @return {*}
         */
        getIframeProxyClientForEndPoint: function (endPointName) {
            var iframeProxyClient = this.endPointsCollection[endPointName].iframeProxy;

            if (!iframeProxyClient) {
                iframeProxyClient = new IframeProxyClient(
                    this.endPointsCollection[endPointName].iframeProxyName);
                this.endPointsCollection[endPointName].iframeProxy = iframeProxyClient;
            }

            return iframeProxyClient;
        },

        /**
         * This function can be used to determine if the endPoint is message container based or not.
         * @param endPointName
         * @return {boolean}
         */
        isEndpointMessageContainerBased: function (endPointName) {
            if (endPointName && this.endPointsCollection[endPointName]) {
                return this.endPointsCollection[endPointName].acceptsMessageContainerMessages;
            } else {
                return true;
            }
        },

        /**
         * Adds messageTypes to the endpoint.
         * @param endPointName
         * @param message
         */
        addMessagesTypesToEndPoint: function (endPointName, message) {
            if (this.endPointsCollection[endPointName] &&
                    this.endPointsCollection[endPointName].messages.indexOf(message) === -1) {
                this.endPointsCollection[endPointName].messages.push(message);
            }
        },

        /**
         * Removes messageTypes from the endPoint.
         * @param endPointName
         * @param message
         */
        removeMessageTypesFromEndPoint: function (endPointName, message) {
            if (this.endPointsCollection[endPointName]) {
                var index = this.endPointsCollection[endPointName].messages.indexOf(message);
                if (index > -1) {
                    this.endPointsCollection[endPointName].messages.splice(index, 1);
                }
            }
        }

    });
});
